import React, { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Gamepad2, CreditCard, Webhook, ShieldAlert } from 'lucide-react';
import { PageHeader } from '@kioskgaming/ui';
import AdminTabs from '../../ui/AdminTabs';
import IntegrationLogFiltersPanel from './IntegrationLogFilters';
import IntegrationLogTable from './IntegrationLogTable';
import IntegrationLogDetailSheet from './IntegrationLogDetailSheet';
import {
  IntegrationLogFilters,
  IntegrationLogListItem,
  IntegrationLogTab,
  IntegrationOutboundLogDetail,
  IntegrationWebhookLogDetail,
} from '../../../types';
import {
  useIntegrationGameLogDetail,
  useIntegrationGameLogs,
  useIntegrationPaymentLogDetail,
  useIntegrationPaymentLogs,
  useIntegrationWebhookLogDetail,
  useIntegrationWebhookLogs,
} from '../../../hooks/useApi';
import {
  buildDefaultLogRange,
  filtersToQueryParams,
  normalizeIntegrationLogPagination,
  parseFiltersFromSearchParams,
} from './utils';

const TAB_ITEMS = [
  { id: 'game' as const, label: 'Game providers', icon: Gamepad2 },
  { id: 'payment' as const, label: 'Payment APIs', icon: CreditCard },
  { id: 'webhooks' as const, label: 'Webhooks', icon: Webhook },
];

const IntegrationLogsManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = useMemo(() => parseFiltersFromSearchParams(searchParams), [searchParams]);

  const [tab, setTab] = useState<IntegrationLogTab>(initial.tab);
  const [filters, setFilters] = useState<IntegrationLogFilters>(initial.filters);
  const [appliedFilters, setAppliedFilters] = useState<IntegrationLogFilters>(initial.filters);

  React.useEffect(() => {
    const parsed = parseFiltersFromSearchParams(searchParams);
    setTab(parsed.tab);
    setFilters(parsed.filters);
    setAppliedFilters(parsed.filters);
  }, [searchParams]);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const queryParams = useMemo(
    () => filtersToQueryParams(tab, appliedFilters),
    [tab, appliedFilters],
  );

  const gameQuery = useIntegrationGameLogs(queryParams, tab === 'game');
  const paymentQuery = useIntegrationPaymentLogs(queryParams, tab === 'payment');
  const webhookQuery = useIntegrationWebhookLogs(queryParams, tab === 'webhooks');

  const activeQuery =
    tab === 'game' ? gameQuery : tab === 'payment' ? paymentQuery : webhookQuery;

  const gameDetail = useIntegrationGameLogDetail(
    tab === 'game' && detailOpen && selectedId ? String(selectedId) : null,
  );
  const paymentDetail = useIntegrationPaymentLogDetail(
    tab === 'payment' && detailOpen && selectedId ? String(selectedId) : null,
  );
  const webhookDetail = useIntegrationWebhookLogDetail(
    tab === 'webhooks' && detailOpen && selectedId != null ? selectedId : null,
  );

  const listData = activeQuery.data?.data;
  const logs: IntegrationLogListItem[] = listData?.logs ?? [];
  const pagination = normalizeIntegrationLogPagination(listData?.pagination);

  const detailQuery =
    tab === 'game' ? gameDetail : tab === 'payment' ? paymentDetail : webhookDetail;

  const detailLog: IntegrationOutboundLogDetail | IntegrationWebhookLogDetail | null =
    detailQuery.data?.data?.log ?? null;

  const handleTabChange = (next: IntegrationLogTab) => {
    setTab(next);
    setSelectedId(null);
    setDetailOpen(false);
    const nextFilters = { ...filters, page: 1 };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setSearchParams({ tab: next });
  };

  const handleSearch = useCallback(() => {
    setAppliedFilters({ ...filters, page: 1 });
    const params: Record<string, string> = { tab };
    if (filters.userId) params.userId = filters.userId;
    if (filters.gameProviderId) params.gameProviderId = filters.gameProviderId;
    if (filters.transactionId) params.transactionId = filters.transactionId;
    setSearchParams(params);
  }, [filters, tab, setSearchParams]);

  const handlePageChange = (page: number) => {
    const next = { ...appliedFilters, page };
    setAppliedFilters(next);
    setFilters(next);
  };

  const handleViewDetail = (log: IntegrationLogListItem) => {
    setSelectedId(log.id);
    setDetailOpen(true);
  };

  const handleResetRange = () => {
    const range = buildDefaultLogRange();
    const next = { ...filters, ...range, page: 1 };
    setFilters(next);
    setAppliedFilters(next);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform API logs"
        description="Outbound calls to game and payment providers, plus inbound payment webhooks. Super admin only."
      />

      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
        <p>
          Logs may contain masked sensitive data. Use only for internal debugging. Maximum search
          range is 31 days.
        </p>
      </div>

      <AdminTabs tabs={TAB_ITEMS} value={tab} onChange={handleTabChange} variant="underline" />

      <IntegrationLogFiltersPanel
        tab={tab}
        filters={filters}
        onChange={setFilters}
        onSearch={handleSearch}
        isSearching={activeQuery.isFetching}
      />

      <div className="flex justify-end">
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={handleResetRange}
        >
          Reset to last 24 hours
        </button>
      </div>

      {activeQuery.isError && (
        <p className="text-sm text-red-600">
          {(activeQuery.error as Error)?.message || 'Failed to load logs'}
        </p>
      )}

      <IntegrationLogTable
        tab={tab}
        logs={logs}
        pagination={pagination}
        isLoading={activeQuery.isLoading}
        onPageChange={handlePageChange}
        onViewDetail={handleViewDetail}
      />

      <IntegrationLogDetailSheet
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedId(null);
        }}
        tab={tab}
        log={detailLog}
        isLoading={detailQuery.isLoading}
      />
    </div>
  );
};

export default IntegrationLogsManagement;
